import { useCallback, useContext, useReducer, useRef } from 'react'
import type { ReactNode } from 'react'
import type { FieldBoundaryContextState } from './FieldBoundaryContext'
import FieldBoundaryContext from './FieldBoundaryContext'
import DataContext from '../Context'
import type { Path } from '../../types'

export type FieldBoundaryProviderProps = {
  showErrors?: boolean
  onPathError?: (path: Path, error: Error) => void
  children: ReactNode
}

export default function FieldBoundaryProvider(
  props: FieldBoundaryProviderProps
) {
  const { showErrors = undefined, onPathError = null, children } = props
  const [, forceUpdate] = useReducer(() => ({}), {})
  const { showAllErrors } = useContext(DataContext)

  const onPathErrorRef = useRef(onPathError)
  onPathErrorRef.current = onPathError
  const errorsRef = useRef<Record<Path, boolean>>({})
  const showBoundaryErrorsRef =
    useRef<FieldBoundaryContextState['showBoundaryErrors']>(showErrors)
  const hasError = Object.keys(errorsRef.current).length > 0
  const verifyFieldError = useCallback(() => {
    return Object.keys(errorsRef.current).length > 0
  }, [])
  const hasSubmitError = showAllErrors && hasError

  const setFieldError = useCallback((path: Path, error: Error) => {
    if (error) {
      errorsRef.current[path] = !!error
    } else {
      delete errorsRef.current?.[path]
    }
    forceUpdate()
    onPathErrorRef.current?.(path, error)
  }, [])

  const hasVisibleErrorRef = useRef<Map<Path, boolean>>(new Map())
  const revealError = useCallback((path: Path, hasError: boolean) => {
    if (hasError) {
      hasVisibleErrorRef.current.set(path, hasError)
    } else {
      hasVisibleErrorRef.current.delete(path)
    }
    forceUpdate()
  }, [])

  const setShowBoundaryErrors: FieldBoundaryContextState['setShowBoundaryErrors'] =
    useCallback((showBoundaryErrors) => {
      showBoundaryErrorsRef.current = showBoundaryErrors
        ? Date.now() // in order to renew the internal dependency "showBoundaryErrors"
        : false
      forceUpdate()
    }, [])

  const context: FieldBoundaryContextState = {
    hasError,
    hasSubmitError,
    hasVisibleError: hasVisibleErrorRef.current.size > 0,
    errorsRef,
    showBoundaryErrors: showBoundaryErrorsRef.current,
    verifyFieldError,
    setShowBoundaryErrors,
    setFieldError,
    revealError,
  }

  return (
    <FieldBoundaryContext value={context}>{children}</FieldBoundaryContext>
  )
}
